import { useEffect, useRef, useState } from 'react';

import { useWorkspaceTimeZone } from '@/features/workspace/useWorkspace';
import { Button } from './Button';
import { EmptyState } from './Card';
import { auditActionLabel } from './vocabulary';
import { formatInZone, zonedToInstant } from './zonedTime';

/** How many changes show before "Show more", and how many each press adds. */
const PAGE_SIZE = 20;

/** A calendar day with no time of day, such as a service record's. */
const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_FORMAT = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeZone: 'UTC' });

/** One change to a record, as a hub already holds it. */
export interface RecordHistoryEntry {
  /** The audit action code. It is worded through the vocabulary, never shown as it is. */
  readonly action: string;
  /** Who did it, as a name a person would know. */
  readonly actorLabel: string;
  /**
   * When it happened: an ISO-8601 instant, or a calendar day (`2026-03-02`) for a change that has
   * a day and no time of day. A day is worded without a time or a zone, and sorts as the start of
   * that day in the workspace's zone.
   */
  readonly occurredAt: string;
  /** What the person wrote with the change, if anything. */
  readonly note?: string | null;
}

export interface RecordHistoryProps {
  readonly entries: readonly RecordHistoryEntry[];
}

function isCalendarDay(occurredAt: string): boolean {
  return CALENDAR_DAY.test(occurredAt) && !Number.isNaN(Date.parse(occurredAt));
}

function timeOf(entry: RecordHistoryEntry, timeZone: string): number {
  const instant = isCalendarDay(entry.occurredAt)
    ? (zonedToInstant(`${entry.occurredAt}T00:00`, timeZone) ?? entry.occurredAt)
    : entry.occurredAt;
  const at = Date.parse(instant);
  return Number.isNaN(at) ? Number.NEGATIVE_INFINITY : at;
}

function whenOf(occurredAt: string, timeZone: string): string {
  if (isCalendarDay(occurredAt)) {
    return ` on ${DAY_FORMAT.format(new Date(occurredAt))}`;
  }
  if (Number.isNaN(Date.parse(occurredAt))) {
    return '';
  }
  return ` on ${formatInZone(occurredAt, timeZone)}`;
}

function noteOf(entry: RecordHistoryEntry): string | null {
  const note = entry.note?.trim();
  return note === undefined || note === '' ? null : note;
}

/**
 * One change as a plain sentence: what was done, by whom, and when.
 *
 * @param entry the change
 * @param timeZone the workspace's IANA time zone, which the time is read in
 * @returns for example "Job created by Dana Okoro on 19 Oct 2026, 17:00 GMT-5."
 */
export function describeEntry(entry: RecordHistoryEntry, timeZone: string): string {
  const label = auditActionLabel(entry.action);
  const what = label === '' ? 'Changed' : label;
  const actor = entry.actorLabel.trim();
  const who = actor === '' ? '' : ` by ${actor}`;
  return `${what}${who}${whenOf(entry.occurredAt, timeZone)}.`;
}

/**
 * A record's history, newest first, one sentence a change.
 *
 * Presentational: it is given the entries a record hub already has, such as a deal's audit trail,
 * approvals or a customer's timeline, and words them. An action is worded through the audit
 * vocabulary and the time is read in the workspace's zone; nothing else of the stored event, and
 * above all not its payload, is shown. The first twenty changes show, and "Show more" adds twenty
 * at a time, moving focus to the first one it added so a keyboard user is not left behind.
 *
 * Needs the signed-in session, for the zone.
 *
 * @param props see {@link RecordHistoryProps}
 * @returns the list, or an empty state when there is no history
 */
export function RecordHistory({ entries }: RecordHistoryProps): React.JSX.Element {
  const timeZone = useWorkspaceTimeZone();
  const [shown, setShown] = useState(PAGE_SIZE);
  const [focusFrom, setFocusFrom] = useState<number | null>(null);
  const firstAdded = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (focusFrom !== null) {
      firstAdded.current?.focus();
    }
  }, [focusFrom]);

  if (entries.length === 0) {
    return (
      <EmptyState
        icon="audit"
        title="No history yet"
        description="Changes to this record will be listed here, newest first."
      />
    );
  }

  const ordered = entries.toSorted((a, b) => timeOf(b, timeZone) - timeOf(a, timeZone));
  const visible = ordered.slice(0, shown);
  const showMore = () => {
    setFocusFrom(shown);
    setShown(shown + PAGE_SIZE);
  };

  return (
    <div className="fo-record-history">
      <ol className="fo-record-history__list">
        {visible.map((entry, index) => {
          const note = noteOf(entry);
          return (
            <li
              key={`${entry.occurredAt}:${entry.action}:${String(index)}`}
              className="fo-record-history__entry"
              ref={index === focusFrom ? firstAdded : undefined}
              tabIndex={index === focusFrom ? -1 : undefined}
            >
              <p className="fo-record-history__sentence">{describeEntry(entry, timeZone)}</p>
              {note === null ? null : <p className="fo-record-history__note">{note}</p>}
            </li>
          );
        })}
      </ol>
      {ordered.length > visible.length ? (
        <div className="fo-record-history__more">
          <p>{`Showing ${String(visible.length)} of ${String(ordered.length)} changes.`}</p>
          <Button variant="secondary" onClick={showMore}>
            Show more
          </Button>
        </div>
      ) : null}
    </div>
  );
}
